/**
 * Attendance status codes — v109.9.136 canonical set (only these 13):
 *
 *   P       Present               ≥ 9 hrs punched
 *   A       Absent                No punch or < 4 hrs
 *   CL      Casual Leave          Paid (approved)
 *   SL      Sick Leave            Paid (approved)
 *   EL      Earned Leave          Paid (approved)
 *   LWP     Leave Without Pay     Deduction (approved)
 *   HD-CL   Half Day Casual Leave Paid + worked 4-9 hrs
 *   HD-SL   Half Day Sick Leave   Paid + worked 4-9 hrs
 *   HD-EL   Half Day Earned Leave Paid + worked 4-9 hrs
 *   HD-LWP  Half Day LWP          Deduction + worked 4-9 hrs
 *   HD-A    Half Day Present      Punched 4-9 hrs, no leave
 *   WO      Weekly Off            Paid (Sunday)
 *   PH      Public Holiday        Paid
 *
 * All legacy status labels ("Half Day", "HD-P", "Casual Leave" fulltext,
 * etc.) are normalized to canonical codes below.
 */

// Full name / legacy code → canonical code
const FULL_TO_CODE = {
  "Present": "P",
  "Absent": "A",
  "Absent (no record)": "A",
  "Casual Leave": "CL",
  "Sick Leave": "SL",
  "Earned Leave": "EL",
  "Unpaid Leave": "LWP",
  "Leave Without Pay": "LWP",
  "Half Day": "HD-A",         // legacy → HD-A
  "Half Day Present": "HD-A",
  "HD-P": "HD-A",              // legacy alias → HD-A
  "Half Day LWP": "HD-LWP",
  "Half Day Casual Leave": "HD-CL",
  "Half Day Sick Leave": "HD-SL",
  "Half Day Earned Leave": "HD-EL",
  "Weekly Off": "WO",
  "Public Holiday": "PH",
};

/** Canonical code → full display label. */
export const CODE_TO_FULL = {
  "P": "Present",
  "A": "Absent",
  "CL": "Casual Leave",
  "SL": "Sick Leave",
  "EL": "Earned Leave",
  "LWP": "Leave Without Pay",
  "HD-A": "Half Day Present",
  "HD-CL": "Half Day Casual Leave",
  "HD-SL": "Half Day Sick Leave",
  "HD-EL": "Half Day Earned Leave",
  "HD-LWP": "Half Day LWP",
  "WO": "Weekly Off",
  "PH": "Public Holiday",
};

/** Set of canonical codes. */
export const CANONICAL_CODES = new Set(Object.keys(CODE_TO_FULL));

/** Leave-code subset (triggers auto-generate-leave when picked in edit). */
export const LEAVE_CODES = new Set([
  "CL", "SL", "EL", "LWP",
  "HD-CL", "HD-SL", "HD-EL", "HD-LWP",
]);

/** Direct-set subset (safe to write straight to attendance record). */
export const DIRECT_CODES = new Set(["P", "A", "HD-A", "WO", "PH"]);

export function statusToCode(status, row) {
  // Server-provided `status_code` is the source of truth.
  if (row && row.status_code) {
    const c = String(row.status_code).toUpperCase();
    // Migrate legacy HD-P → HD-A on read as well.
    return c === "HD-P" ? "HD-A" : c;
  }
  if (!status) return "-";
  const s = String(status);
  if (s in FULL_TO_CODE) return FULL_TO_CODE[s];
  // Already a canonical code.
  const up = s.toUpperCase();
  if (CANONICAL_CODES.has(up)) return up;
  if (up === "HD-P") return "HD-A";
  // Holiday-worked / Sunday-worked labels
  if (s.startsWith("Holiday")) return "PH";
  if (s.startsWith("Sunday")) return "WO";
  return s.length <= 4 ? s : s.slice(0, 4);
}

/** Full label for a canonical code. */
export function codeToLabel(code) {
  return CODE_TO_FULL[String(code || "").toUpperCase()] || code || "-";
}

/**
 * Format an attendance punch time as "HH:MM AM/PM" (IST — the stored value is
 * treated as a naive local time in the Asia/Kolkata timezone).
 *
 *   "2026-02-06T09:15:00"  →  "9:15 AM"
 *   "16:30"                →  "4:30 PM"
 *   null/undefined         →  null
 */
/**
 * Format a punch value ("2026-02-06T09:15:00Z" or "HH:MM") into HH:MM:SS AM/PM
 * in IST. Bare `HH:MM` strings are treated as local wall-clock and padded to
 * `HH:MM:00 AM/PM`.
 *
 * Examples:
 *   "2026-02-06T09:15:00Z"  →  "02:45:00 PM"  (IST)
 *   "16:30"                 →  "04:30:00 PM"
 *   null/undefined          →  null
 *
 * v109.9.43 — Standardised across the whole app; always HH:MM:SS AM/PM in IST.
 */
export function fmt12h(v) {
  if (!v) return null;
  const s = String(v);
  // Bare HH:MM or HH:MM:SS wall-clock strings — format without TZ conversion.
  const bare = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (bare) {
    let h = Number(bare[1]);
    const m = bare[2];
    const sec = bare[3] || "00";
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12; if (h === 0) h = 12;
    return `${String(h).padStart(2, "0")}:${m}:${sec} ${ampm}`;
  }
  // Full datetime — convert to IST.
  try {
    const d = new Date(s);
    if (isNaN(d)) return null;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    }).formatToParts(d);
    const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    const period = (p.dayPeriod || "").toUpperCase().replace(/\./g, "");
    return `${p.hour}:${p.minute}:${p.second} ${period}`;
  } catch { return null; }
}

/**
 * Format decimal hours as "Xh YYm" (e.g. 8.5 → "8h 30m", 0 → "0h 00m").
 */
export function fmtHoursHM(hoursDecimal) {
  const n = Number(hoursDecimal || 0);
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/**
 * Parse a stored punch value ("2026-02-06T09:15:00" or "HH:MM") into
 * { h12, mm, ampm } → { h12: 1-12, mm: "00"-"59", ampm: "AM"|"PM" }.
 * Returns nulls when input is empty/invalid.
 */
export function parseTimeAmPm(v) {
  if (!v) return { h12: "", mm: "", ampm: "AM" };
  const s = String(v);
  const hhmm = s.includes("T") ? s.slice(11, 16) : s.slice(0, 5);
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return { h12: "", mm: "", ampm: "AM" };
  let h = Number(m[1]);
  const mm = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return { h12: h, mm, ampm };
}

/**
 * Encode h12/mm/ampm back into "HH:MM" 24-hour format.
 * Returns "" for missing pieces.
 */
export function encodeTimeAmPm(h12, mm, ampm) {
  const h = Number(h12);
  if (!h || h < 1 || h > 12) return "";
  const m = String(mm ?? "00").padStart(2, "0");
  let hh = h % 12;
  if ((ampm || "AM").toUpperCase() === "PM") hh += 12;
  return `${String(hh).padStart(2, "0")}:${m}`;
}

/**
 * Compute hours worked between two "HH:MM" or ISO strings.
 * Returns decimal hours, e.g. "09:15" → "18:45" → 9.5.
 */
export function computeHoursWorked(inV, outV) {
  const a = parseTimeAmPm(inV);
  const b = parseTimeAmPm(outV);
  if (!a.h12 || !b.h12) return 0;
  const to24 = ({ h12, mm, ampm }) => {
    let h = Number(h12) % 12;
    if ((ampm || "AM").toUpperCase() === "PM") h += 12;
    return h * 60 + Number(mm || 0);
  };
  const diff = to24(b) - to24(a);
  return Math.max(0, diff / 60);
}


export function classifyStatus(d) {
  // v109.9.128 — Server-provided `status_code` wins; classify from that.
  const code = String(d?.status_code || "").toUpperCase();
  const s = String(d?.status || "");
  const isSunday = !!d?.is_sunday;
  const isSunWorked = isSunday && (d?.punch_in || d?.punch_out || d?.report_submitted);
  const isHDCode = /^HD-(A|P|LWP|CL|SL|EL)$/i.test(code || s);
  const isPaidLeave = code === "CL" || code === "SL" || code === "EL"
    || ["Paid Leave", "On Leave", "Casual Leave", "Sick Leave", "Earned Leave"].includes(s);
  const isUnpaidLeave = code === "LWP" || s === "Unpaid Leave" || s === "Leave Without Pay" || s.toUpperCase() === "LWP";
  const isHalfLeave = ["HD-CL", "HD-SL", "HD-EL"].includes(code) || s === "Half Day (Leave)";
  const isHalfUnpaid = code === "HD-LWP";
  const isHalfPresent = code === "HD-A" || code === "HD-P";
  const isPresent = code === "P" || s === "Present";
  const isHalf = code === "HD" || isHalfLeave || isHalfUnpaid || isHalfPresent || s === "Half Day";
  const isAbsent = code === "A" || s === "Absent" || s === "Absent (no record)";
  const isPureHoliday = code === "PH" || code === "WO" || d?.is_holiday
    || s === "Weekly Off" || s === "Public Holiday" || (isSunday && !isSunWorked);
  return { isSunday, isSunWorked, isPaidLeave, isUnpaidLeave, isHalfLeave, isHalf, isPresent, isAbsent, isPureHoliday, isHDCode, isHalfPresent, isHalfUnpaid };
}

export function statusCellClasses(cls) {
  const container =
    cls.isSunWorked   ? "bg-purple-100 border-purple-500 dark:bg-purple-950/40 dark:border-purple-500" :
    cls.isPureHoliday ? "bg-blue-50 border-blue-300 dark:bg-blue-950/30 dark:border-blue-700" :
    cls.isUnpaidLeave ? "bg-orange-50 border-orange-400 dark:bg-orange-950/30 dark:border-orange-600" :
    cls.isPaidLeave   ? "bg-slate-100 border-slate-400 dark:bg-slate-800 dark:border-slate-600" :
    cls.isHalf        ? "bg-yellow-50 border-yellow-400 dark:bg-yellow-950/30 dark:border-yellow-600" :
    cls.isAbsent      ? "bg-rose-50 border-rose-400 dark:bg-rose-950/30 dark:border-rose-600" :
    cls.isPresent     ? "bg-emerald-50 border-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-600" :
                        "bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-700";

  const chip =
    cls.isSunWorked   ? "bg-purple-600 text-white" :
    cls.isPureHoliday ? "bg-blue-600 text-white" :
    cls.isUnpaidLeave ? "bg-orange-600 text-white" :
    cls.isPaidLeave   ? "bg-slate-500 text-white" :
    cls.isHalf        ? "bg-yellow-500 text-black" :
    cls.isAbsent      ? "bg-rose-600 text-white" :
    cls.isPresent     ? "bg-emerald-600 text-white" :
                        "bg-slate-300 text-slate-800";
  return { container, chip };
}


/**
 * v109.9.42 — Return today's calendar date in IST as "YYYY-MM-DD".
 *
 * We build the ISO string component-by-component from the localised Date
 * fields (via `Intl.DateTimeFormat` in `en-CA` when available, falling back
 * to a manual computation using the fixed +05:30 offset). This is robust
 * against browsers that render `toLocaleDateString("en-CA", ...)` without
 * zero-padded month/day (older Safari) which would break lexicographic
 * comparison with server-provided `YYYY-MM-DD` strings.
 */
export function todayIsoInIST() {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(now);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    const y = get("year"), m = get("month"), d = get("day");
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch { /* fall through */ }
  // Fallback — manual IST (UTC + 5h30m) construction.
  const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000) - now.getTimezoneOffset() * 60000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Returns true if the given per-day row's date is TODAY (IST) or earlier.
 * Server sends `YYYY-MM-DD` strings; we defensively `slice(0, 10)` to strip
 * any accidental trailing time component.
 */
export function isPastOrToday(dayLike) {
  if (!dayLike?.date) return false;
  return String(dayLike.date).slice(0, 10) <= todayIsoInIST();
}
